import { axiosPrivate } from "../axios";
import { useEffect } from "react";
import useRefreshToken from "../../hooks/useRefreshToken";
import useAuth from "../../hooks/useAuth";
import { TEXT_MSGS } from "../../utility/constant";

export const SESSION_LOGIN_EVENT = "vt-session-login";
export const INVALIDATED_SESSIONS_EVENT = "vt-invalidated-sessions";
export const SESSION_LOGOUT_EMAIL_KEY = "vt_session_logout_email";

export function notifySameUserLoggedOut(emailId) {
    const email = String(emailId ?? "")
        .trim()
        .toLowerCase();
    if (!email) {
        return;
    }
    try {
        localStorage.setItem(
            SESSION_LOGOUT_EMAIL_KEY,
            JSON.stringify({
                email,
                eventId: crypto.randomUUID(),
                timestamp: Date.now(),
            }),
        );
    } catch {
        // Ignore storage errors.
    }
}

export function resolveSessionLoginMessage(errorOrMessage) {
    if (typeof errorOrMessage === "string") {
        const text = errorOrMessage.trim();
        return text || TEXT_MSGS.LOGIN_POPUP_MSG;
    }

    if (
        errorOrMessage != null &&
        typeof errorOrMessage === "object" &&
        !errorOrMessage.response &&
        typeof errorOrMessage.message === "string"
    ) {
        const text = errorOrMessage.message.trim();
        return text || TEXT_MSGS.LOGIN_POPUP_MSG;
    }

    const apiMessage = errorOrMessage?.response?.data?.message;
    if (apiMessage != null && String(apiMessage).trim()) {
        return String(apiMessage).trim();
    }

    return TEXT_MSGS.LOGIN_POPUP_MSG;
}

/** `last_login` from a 410 refresh (or equivalent) response body. */
export function resolveSessionLoginLastLogin(errorOrMessage) {
    const fromResponse = errorOrMessage?.response?.data?.last_login;
    if (fromResponse != null && typeof fromResponse === "object") {
        return fromResponse;
    }

    if (
        errorOrMessage != null &&
        typeof errorOrMessage === "object" &&
        !errorOrMessage.response &&
        errorOrMessage.lastLogin != null &&
        typeof errorOrMessage.lastLogin === "object"
    ) {
        return errorOrMessage.lastLogin;
    }

    return undefined;
}

export function openSessionLoginModal(errorOrMessage) {
    if (typeof window === "undefined") {
        return;
    }

    const message = resolveSessionLoginMessage(errorOrMessage);
    const lastLogin = resolveSessionLoginLastLogin(errorOrMessage);

    window.dispatchEvent(
        new CustomEvent(SESSION_LOGIN_EVENT, {
            detail: { message, lastLogin },
        }),
    );
}

export function notifyInvalidatedSessions(sessions) {
    if (typeof window === "undefined") {
        return;
    }
    if (!Array.isArray(sessions) || sessions.length === 0) {
        return;
    }

    window.dispatchEvent(
        new CustomEvent(INVALIDATED_SESSIONS_EVENT, {
            detail: { sessions },
        }),
    );
}

const useAxiosPrivate = () => {
    const refresh = useRefreshToken();
    const { auth } = useAuth();

    useEffect(() => {
        const requestIntercept = axiosPrivate.interceptors.request.use(
            (config) => {
                if (!config.headers["Authorization"]) {
                    config.headers["Authorization"] = auth?.accessToken;
                }
                return config;
            },
            (error) => Promise.reject(error),
        );

        const responseIntercept = axiosPrivate.interceptors.response.use(
            (response) => response,

            async (error) => {
                const status = error?.response?.status;
                const prevRequest = error?.config;

                // /refresh failures: only 410 = session revoked → session modal.
                if (!prevRequest || prevRequest.url?.includes("refresh")) {
                    if (status === 410) {
                        openSessionLoginModal(error);
                    }
                    return Promise.reject(error);
                }

                // Expired access token: try refresh once (do not open modal yet).
                if ((status === 401 || status === 403) && !prevRequest._retry) {
                    prevRequest._retry = true;

                    try {
                        const newAccessToken = await refresh({
                            accessTokenExpired: true,
                        });

                        prevRequest.headers = prevRequest.headers || {};
                        prevRequest.headers["Authorization"] = newAccessToken;

                        return axiosPrivate(prevRequest);
                    } catch (refreshError) {
                        const refreshStatus = refreshError?.response?.status;

                        if (refreshStatus === 410) {
                            openSessionLoginModal(refreshError);
                        }

                        return Promise.reject(error);
                    }
                }

                return Promise.reject(error);
            },
        );

        return () => {
            axiosPrivate.interceptors.request.eject(requestIntercept);
            axiosPrivate.interceptors.response.eject(responseIntercept);
        };
    }, [auth?.accessToken, refresh]);

    return axiosPrivate;
};

export default useAxiosPrivate;
